import React, { useState, useEffect, useContext, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// CONTEXTE AUTH
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BusinessProvider } from './contexts/BusinessContext';

// COMPOSANTS AUTH
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import EmailVerificationPending from './components/auth-v2/EmailVerificationPending';

// COMPOSANTS ET MODULES
import BoutiqueModule from './modules/BoutiqueModule';
import BoutiqueDetail from './modules/BoutiqueDetail';
import CRMModule from './modules/CRMModule';
import CommandesModule from './modules/CommandesModule';
import InventaireModule from './modules/InventaireModule';
import ErrorBoundary from './ErrorBoundary';
import PublicProductPage from './modules/PublicProductPage';
import PublicStorefront from './modules/PublicStorefront';
import LandingPage from './components/LandingPage';
import { fetchCurrentUserRole } from './services/apiClient';
import Layout from './components/layout/AppLayout';
import Dashboard from './components/dashboard/Dashboard';
import tokens from './styles/tokens';
import EmptyState from './components/ui/EmptyState';

// Routes secondaires chargées à la demande (React.lazy) — réduit le bundle
// initial. Les écrans du parcours principal (dashboard, boutiques, stock,
// commandes, clients) restent en chargement statique.
const FactureModule = lazy(() => import('./modules/FactureModule'));
const PublicInvoiceVerification = lazy(() => import('./modules/facture/PublicInvoiceVerification'));
const ParametresModule = lazy(() => import('./modules/ParametresModule'));
const MonCompteModule = lazy(() => import('./modules/MonCompteModule'));
const ComptabiliteModule = lazy(() => import('./modules/ComptabiliteModule'));
const ReceivablesModule = lazy(() => import('./modules/ReceivablesModule'));
const BibliothequeModule = lazy(() => import('./modules/BibliothequeModule'));
const PlatformAdminModule = lazy(() => import('./modules/PlatformAdminModule'));

// AJOUT: Créez le client (en dehors du composant)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  },
});

export const DEVISES = [
  { id: 'xof', nom: 'Franc CFA', symbole: 'FCFA', code: 'XOF' },
  { id: 'eur', nom: 'Euro', symbole: '€', code: 'EUR' },
  { id: 'usd', nom: 'Dollar', symbole: '$', code: 'USD' }
];

// TAUX DE CONVERSION (pour référence globale)
export const TAUX_CHANGE = {
  'XOF_EUR': 0.0015,
  'XOF_USD': 0.0016,
  'EUR_XOF': 655.95,
  'USD_XOF': 600.00,
  'EUR_USD': 1.08,
  'USD_EUR': 0.92
};

export const Icon = ({ name, size = 20 }) => {
  const icons = {
    home: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
    facture: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    boutique: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z",
    compta: "M12 8c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3zm0 0v8m-4-4h8",
    crm: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2a3 3 0 00-3-3H10a3 3 0 00-3 3v2",
    settings: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35",
    menu: "M4 6h16M4 12h16M4 18h16",
    package: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4",
    qrcode: "M4 4h6v6H4V4zm10 0h6v6h-6V4zm-10 10h6v6H4v-6zm10 0h6v6h-6v-6z",
    bibliotheque: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
    sun: "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z",
    moon: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z",
    trendUp: "M23 6l-9.5 9.5-5-5L1 18",
    users: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
    dollar: "M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
    sales: "M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z M3 6h18 M16 10a4 4 0 0 1-8 0",
    team: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75",
    grid: "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z",
    chevronLeft: "M15 18l-6-6 6-6",
    chevronRight: "M9 18l6-6-6-6",
    logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9",
    alert: "M12 9v4 M12 17h.01 M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={icons[name] || ''} />
    </svg>
  );
};

export const AppContext = React.createContext();

function AppProvider({ children }) {
  const { currentUser } = useAuth();

  // CACHE / PRÉFÉRENCE LOCALE (P0.9): purement cosmétique, sans risque si perdue.
  // localStorage reste approprié ici, pas de migration prévue.
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('nsara_darkmode') === 'true');

  const theme = darkMode ? {
    primary: "#2F8F68", 
    secondary: "#FF6B35", 
    background: "#121212", 
    text: "#ffffff", 
    card: "#1e1e1e", 
    border: "#333"
  } : {
    primary: "#1B6B4A", 
    secondary: "#FF6B35", 
    background: tokens.colors.background,
    text: tokens.colors.text,
    card: "#ffffff", 
    border: tokens.colors.border
  };

  // DONNÉES MÉTIER (P0.9): actuellement encore persistées uniquement en localStorage
  // (clé nsara_user_<uid>). Ce n'est PLUS considéré comme la base de données
  // définitive de NSARA — voir backend/sql/0001_business_shops.sql et
  // backend/routes/{business,shops}.js pour les fondations serveur (Supabase/Postgres)
  // qui remplaceront progressivement ce bloc, entité par entité, dans la prochaine
  // mission ("P0 MÉTIER — connecter Produit → Stock → Vente → Facture → Dashboard").
  // Ne pas ajouter de nouvelles données critiques ici sans plan de migration serveur.
  const [user, setUser] = useState(() => {
    const userKey = currentUser ? `nsara_user_${currentUser.uid}` : 'nsara_user_guest';
    const saved = localStorage.getItem(userKey);

    const defaultEmptyUser = {
      id: currentUser?.uid || uuidv4(),
      nom: currentUser?.displayName || "Nouvel Utilisateur",
      email: currentUser?.email || "",
      factures: [],
      clients: [],
      commandes: [],
      inventaire: [],
      boutiques: [],
      devise: DEVISES[0],
      tva: 18,
      nomEntreprise: '',
      slogan: '',
      telephone: '',
      siteWeb: '',
      logo: null,
      couverture: null,
      reseaux: [],
      limites: {
        maxUtilisateurs: '',
        maxBoutiques: '',
        stockageMax: ''
      }
    };

    if (!saved) return defaultEmptyUser;
    try {
      const parsed = JSON.parse(saved);
      return { ...defaultEmptyUser, ...parsed };
    } catch (e) { 
      return defaultEmptyUser; 
    }
  });

  useEffect(() => { 
    localStorage.setItem('nsara_darkmode', darkMode); 
  }, [darkMode]);

  // Sauvegarde automatique isolée pour chaque utilisateur (cache local — voir
  // le commentaire "DONNÉES MÉTIER" ci-dessus: non synchronisé entre appareils,
  // perdu si le stockage du navigateur est effacé).
  useEffect(() => {
    const userKey = currentUser ? `nsara_user_${currentUser.uid}` : 'nsara_user_guest';
    localStorage.setItem(userKey, JSON.stringify(user));
  }, [user, currentUser]);

  return (
    <AppContext.Provider value={{ user, setUser, theme, DEVISES, TAUX_CHANGE, darkMode, setDarkMode }}>
      {children}
    </AppContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: '#1B6B4A',
      color: 'white'
    }}>
      NSARA...
    </div>
  );

  // Aucun bypass: une session utilisateur (Firebase ou personnel local) est requise.
  if (!currentUser) return <Navigate to="/login" />;

  // P-AUTH-EMAIL-VERIFY-2: an authenticated user whose Firebase account is
  // not yet verified must never reach operational children — rendered here
  // instead of a redirect so this works identically for every protected
  // route (deep link, reload, etc.), not just the ones reached via login.
  // emailVerified is Firebase's own signal (see config/supabase.js); Google
  // and completed Magic Link sign-ins are always true, so this never
  // affects them.
  if (!currentUser.emailVerified) return <EmailVerificationPending />;

  return children;
}

// Route racine publique: la vitrine NSARA, à la fois pour les visiteurs non
// connectés ET pour les utilisateurs déjà authentifiés qui cliquent le logo
// NSARA depuis leur espace (voir AppLayout). Auparavant cette route
// redirigeait automatiquement tout utilisateur connecté vers /dashboard,
// ce qui rendait le logo inutilisable comme lien retour vers la vitrine
// (cliquer dessus revenait aussitôt au tableau de bord). LandingPage adapte
// elle-même ses appels à l'action selon l'état de connexion (voir son usage
// de useAuth) plutôt que de dupliquer cette logique ici. Le tableau de bord
// protégé reste exclusivement à /dashboard.
function RootRoute() {
  const { loading } = useAuth();

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: '#1B6B4A',
      color: 'white'
    }}>
      NSARA...
    </div>
  );

  return <LandingPage />;
}

function AdminRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!currentUser) {
      setChecking(false);
      return undefined;
    }
    // Le statut admin est TOUJOURS vérifié côté serveur (GET /api/me) — jamais
    // déduit d'un email/mot de passe/valeur locale côté client.
    fetchCurrentUserRole()
      .then((role) => {
        if (!cancelled) setIsPlatformAdmin(Boolean(role?.isPlatformAdmin));
      })
      .catch(() => {
        if (!cancelled) setIsPlatformAdmin(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => { cancelled = true; };
  }, [currentUser]);

  if (loading || checking) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#1B6B4A', color: 'white' }}>
      NSARA...
    </div>
  );

  if (!currentUser) return <Navigate to="/login" />;
  if (!isPlatformAdmin) return <Navigate to="/dashboard" />;
  return children;
}

function AdminPage() {
  const { user, theme } = useContext(AppContext);
  const { logout } = useAuth();
  return (
    <PlatformAdminModule
      currentUserEmail={user?.email || ''}
      theme={theme}
      onAdminLogout={logout}
    />
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BusinessProvider>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <Suspense fallback={
              <div style={{ padding: 60, display: 'flex', justifyContent: 'center' }} role="status" aria-label="Chargement">
                <div className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
              </div>
            }>
            <Routes>
              {/* ROUTES PUBLIQUES - DOIT ÊTRE AVANT LES ROUTES PROTÉGÉES */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              
              {/* ROUTE RACINE: vitrine publique si déconnecté, dashboard si connecté */}
              <Route path="/" element={<RootRoute />} />
              
              {/* ROUTES PROTÉGÉES */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Layout><Dashboard /></Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/verify/:token" element={<PublicInvoiceVerification />} />
              <Route path="/facture" element={<ProtectedRoute><Layout><FactureModule /></Layout></ProtectedRoute>} />

              <Route path="/boutique" element={
                <ProtectedRoute>
                  <Layout><BoutiqueModule /></Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/boutique/:id" element={
                <ProtectedRoute>
                  <Layout><BoutiqueDetail /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/commandes" element={
                <ProtectedRoute>
                  <Layout><CommandesModule /></Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/crm" element={
                <ProtectedRoute>
                  <Layout><CRMModule /></Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/comptabilite" element={
                <ProtectedRoute>
                  <Layout><ComptabiliteModule /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/creances" element={
                <ProtectedRoute>
                  <Layout><ReceivablesModule /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/inventaire" element={
                <ProtectedRoute>
                  <Layout><InventaireModule /></Layout>
                </ProtectedRoute>
              } />
              
              {/* Mission 01 / Step 4: la gestion du personnel est désactivée tant
                  qu'aucune application serveur des permissions n'existe (voir
                  le rapport d'audit) - le composant reste dans src/modules/
                  mais n'est plus importé ni rendu. */}
              <Route path="/personnel" element={
                <ProtectedRoute>
                  <Layout>
                    <EmptyState
                      title="Gestion du personnel indisponible"
                      description="Cette fonctionnalité est temporairement désactivée : les permissions des employés ne sont pas encore appliquées côté serveur. Elle reviendra une fois un vrai système de comptes/rôles employés en place."
                    />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/bibliotheque" element={
                <ProtectedRoute>
                  <Layout><BibliothequeModule /></Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/parametres" element={
                <ProtectedRoute>
                  <Layout><ParametresModule /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/profil" element={
                <ProtectedRoute>
                  <Layout><MonCompteModule /></Layout>
                </ProtectedRoute>
              } />

              {/* ADMINISTRATION: protection supplémentaire, statut vérifié côté serveur (GET /api/me) */}
              <Route path="/admin" element={
                <AdminRoute>
                  <Layout><AdminPage /></Layout>
                </AdminRoute>
              } />

              {/* PAGE PUBLIQUE V1 : lien produit individuel, sans vitrine ni panier.
                  L'ancienne route vitrine /b/:id (BoutiquePublic) est retirée de
                  l'expérience active — code conservé dans src/modules/legacy/. */}
              <Route path="/p/:publicId" element={<PublicProductPage />} />

              {/* NSARA Stores V2: vitrine publique d'une boutique. Identifiant:
                  l'id (uuid) de la boutique, comme un produit utilise son
                  public_id — aucun slug ni migration (voir routes/publicShops.js
                  côté backend). Aucune authentification: c'est une page vitrine. */}
              <Route path="/s/:shopId" element={<PublicStorefront />} />
              
              {/* ROUTE 404 */}
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
            </Suspense>
          </ErrorBoundary>
        </QueryClientProvider>
        </BusinessProvider>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
